import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import Row, { RowBetween, RowTitled } from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { useCurrency } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useIslandById } from 'hooks/useIslandsApi'
import { isPairPool, usePoolUnderlyingBalances } from 'hooks/usePoolPositionDetails'
import { useRewardVaultEarned } from 'hooks/useRewardVault'
import { useContext, useMemo } from 'react'
import { Text } from 'rebass'
import { IslandApiResponse } from 'state/data/slice'
import { ThemeContext } from 'styled-components'
import { Separator } from 'theme'
import { Address } from 'viem'

import { UnderlyingView } from './WithdrawBalanceConstructorStep'

type WithdrawFromRewardVaultStepProps = {
  amount: CurrencyAmount<Token> | undefined
  rewardVault: Address | undefined
  pool: IslandApiResponse | null
  next: () => void
  onWithdraw: () => void
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

const RewardVaultView = ({ amount, pool }: { amount: CurrencyAmount<Token>; pool: IslandApiResponse | null }) => {
  const { data: island } = useIslandById(amount?.currency?.address?.toLowerCase())
  const currency0 = useCurrency(island?.token0?.id)
  const currency1 = useCurrency(island?.token1?.id)

  const underlyingIslandBalances = usePoolUnderlyingBalances(island, amount, currency0, currency1)
  const _isPairPool = pool ? isPairPool(pool) : false

  const token0 = useCurrency(pool?.token0?.id)
  const token1 = useCurrency(pool?.token1?.id)
  const underlyingPoolBalances = usePoolUnderlyingBalances(pool, amount, token0, token1)

  // Use the appropriate balances based on whether it's an island or V2 pool
  const finalBalances = useMemo(() => {
    if (_isPairPool && underlyingPoolBalances) {
      return {
        amount0: underlyingPoolBalances.amount0,
        amount1: underlyingPoolBalances.amount1,
        currency0: token0,
        currency1: token1,
      }
    }

    return {
      amount0: underlyingIslandBalances?.amount0,
      amount1: underlyingIslandBalances?.amount1,
      currency0,
      currency1,
    }
  }, [underlyingIslandBalances, underlyingPoolBalances, currency0, currency1, token0, token1, _isPairPool])

  return (
    <RowTitled
      reverse
      header={
        <Text fontSize="14px" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {amount?.toSignificant(6)} {amount?.currency?.symbol}
        </Text>
      }
    >
      <UnderlyingView
        amount0={finalBalances.amount0}
        amount1={finalBalances.amount1}
        currency0={finalBalances.currency0}
        currency1={finalBalances.currency1}
      />
    </RowTitled>
  )
}

const WithdrawFromRewardVaultStep = ({
  amount,
  rewardVault,
  pool,
  onWithdraw,
  next,
  ...rest
}: WithdrawFromRewardVaultStepProps) => {
  const theme = useContext(ThemeContext)
  const { account } = useConnection()
  // Fetch earned rewards from RewardVault
  const { earnedRewards } = useRewardVaultEarned(rewardVault, account)

  // Only render if there's an amount to withdraw
  if (!amount || amount.equalTo('0')) {
    return null
  }

  return (
    <ConfirmationTransactionStep {...rest} onConfirm={next}>
      <Column width="100%" gap="8px">
        <LightCard>
          <AutoColumn gap="8px">
            <RewardVaultView amount={amount} pool={pool} />
            <Separator />

            <RowBetween>
              <Row grow="1">
                <Text fontSize={14} color={theme.textSecondary}>
                  Total
                </Text>
              </Row>
              <Row
                grow="0"
                shrink="0"
                width="fit-content"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                justify="flex-end"
              >
                <Text fontSize={14} marginRight="4px" color={theme.textSecondary}>
                  (<FiatValue currencyAmount={amount} />)
                </Text>
                <Text fontSize={14}>
                  {amount?.toSignificant(6)} {amount?.currency?.symbol}
                </Text>
              </Row>
            </RowBetween>
          </AutoColumn>
        </LightCard>

        {earnedRewards && earnedRewards.length > 0 && (
          <LightCard>
            <AutoColumn gap="8px">
              <Text fontSize={14} color={theme.textSecondary}>
                Accrued rewards (not included in unstake)
              </Text>
              {earnedRewards.map((e) => {
                return (
                  <RowBetween key={e.currency.address}>
                    <Row grow="0" shrink="0" width="fit-content">
                      <CurrencyLogo currency={e.currency} size="21px" style={{ marginRight: '8px' }} />
                      <Text fontSize={14}>{e.currency.symbol}</Text>
                    </Row>
                    <Row grow="1" style={{ whiteSpace: 'nowrap', overflow: 'hidden' }} justify="flex-end">
                      <Text fontSize={14} marginRight="4px" color={theme.textSecondary}>
                        (<FiatValue currencyAmount={e} />)
                      </Text>
                      <Text fontSize={14}>{e.toSignificant(6)}</Text>
                    </Row>
                  </RowBetween>
                )
              })}
            </AutoColumn>
          </LightCard>
        )}

        <ButtonPrimary onClick={onWithdraw}>
          Unstake {amount?.toSignificant(6)} {amount?.currency?.symbol}
        </ButtonPrimary>
      </Column>
    </ConfirmationTransactionStep>
  )
}

export default WithdrawFromRewardVaultStep
